import { simpleMode } from '@codemirror/legacy-modes/mode/simple-mode';

// Taken from the latest dql release in the artifactory
// Exported only for tests
export const dqlCommands = [
  'aggregate',
  'append',
  'data',
  'dedup',
  'describe',
  'expand',
  'experimentalFieldsSummary',
  'experimentalLoad',
  'experimentalSave',
  'experimentalTimeChart',
  'failIf',
  'fetch',
  'fields',
  'fieldsAdd',
  'fieldsFlatten',
  'fieldsKeep',
  'fieldsRemove',
  'fieldsRename',
  'fieldsSummary',
  'filter',
  'filterOut',
  'group',
  'internalTimeseriesBins',
  'join',
  'joinNested',
  'limit',
  'lookup',
  'makeTimeseries',
  'parse',
  'search',
  'smartscapeEdges',
  'sort',
  'summarize',
  'timeseries',
];

// The commands need to be sorted by length. Longer names should be at the start of the list
// If `fields` comes before `fieldsAdd` in the array we would match `fieldsAdd` as `fields` without `Add`
// We cannot rely on a empty space after a command either. `fetch/* comment */logs` should match `fetch`
const sortedDqlCommands = dqlCommands.sort((a, b) => b.length - a.length);

const tokenMapping = {
  command: 'keyword',
  comment: 'comment',
  parameter: 'variableName',
  string: 'string',
  number: 'number',
} as const;

const startComment = [
  { regex: /\/\/.*/, token: tokenMapping.comment }, // single line comment
  { regex: /\/\*.*?/, token: tokenMapping.comment, push: 'comment' }, // start of multiline comment
];

export const dql = simpleMode({
  start: [
    ...startComment,
    {
      regex: new RegExp(`(\\s*)((?:${sortedDqlCommands.join('|')})\\s*)`, 'i'),
      token: ['', tokenMapping.command],
      next: 'parameters',
    },
    {
      // We want to tokenize parts of a query as well, so in case of no command
      // in the start state, we go directly to the `parameters` state
      regex: /\s*\b/,
      next: 'parameters',
    },
  ],
  comment: [
    { regex: /.*?\*\//, token: tokenMapping.comment, pop: true }, // end of multiline comment
    { regex: /.*/, token: tokenMapping.comment },
  ],
  parameters: [
    ...startComment,
    { regex: /\w+\s*(?=:)/, token: tokenMapping.parameter }, // parameter, eg: scanLimitGb:
    { regex: /[A-Za-z\]]+[A-Za-z_.0-9]+/ }, // identifier, eg: runs_on or metricsJson
    { regex: /"""/, token: tokenMapping.string, push: 'tripleQuoteString' },
    { regex: /"/, token: tokenMapping.string, push: 'doubleQuoteString' },
    { regex: /`/, token: tokenMapping.string, push: 'backtickString' },
    {
      regex: /(\d+(?:\.\d+)?)(?![.\w])/,
      token: tokenMapping.number,
    },
    {
      regex: /(\[)([0-9]*)(\])/, // array index access, e.g: [0] or [200]
      token: ['', tokenMapping.number, ''], // only highlight number; do not highlight surrounding square brackets
    },
    {
      regex: /(\[)(`(?:[^`\\]|\\.)*`)(])/, // nested property access, e.g: [`app.kubernetes.io/version`]
      token: ['', tokenMapping.string, ''], // only highlight content and backticks; do not highlight surrounding square brackets
    },
    { regex: /\||\[/, next: 'start' }, // start matching a new query syntax after | or [
  ],
  tripleQuoteString: [
    { regex: /[\s\S]*"""/, token: tokenMapping.string, pop: true },
    { regex: /.*/, token: tokenMapping.string },
  ],
  doubleQuoteString: [
    { regex: /"/, token: tokenMapping.string, pop: true },
    // Note on following {0,3000000}: stop at 3 million characters per line to avoid crash.
    // Code-mirror only shows 2 million characters per line anyway.
    { regex: /(?:[^\\"]|\\(?:.|$)){0,3000000}/, token: tokenMapping.string },
  ],
  backtickString: [
    { regex: /`/, token: tokenMapping.string, pop: true },
    { regex: /(?:[^\\`]|\\(?:.|$))*/, token: tokenMapping.string },
  ],
  languageData: {
    name: 'dql',
    dontIndentStates: [],
    indentOnInput: /^\s*\}$/,
    commentTokens: { line: '//', block: { open: '/*', close: '*/' } },
    mergeTokens: false,
  },
});
